import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 6) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
        if (data.status === "expired" || data.payment_status === "conflict") { setState("failed"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-24 text-center" data-testid="booking-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="w-14 h-14 text-primary mx-auto animate-spin" />
          <h1 className="font-display text-3xl font-semibold mt-6">Confirming your payment…</h1>
          <p className="text-muted-foreground mt-2">Please wait, this only takes a moment.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-primary mx-auto" strokeWidth={1.5} />
          <h1 className="font-display text-3xl font-semibold mt-4" data-testid="payment-paid">Deposit received</h1>
          <p className="text-muted-foreground mt-2">Your non-refundable deposit was received and your booking is confirmed. A confirmation email has been sent — your full receipt follows once your provider confirms payment at service.</p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/my-bookings"><Button className="rounded-sm" data-testid="view-bookings-btn">View my bookings</Button></Link>
            <Link to="/browse"><Button variant="outline" className="rounded-sm">Browse more</Button></Link>
          </div>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle className="w-16 h-16 text-destructive mx-auto" strokeWidth={1.5} />
          <h1 className="font-display text-3xl font-semibold mt-4">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="text-muted-foreground mt-2">
            {state === "timeout" ? "We couldn't confirm the payment yet. Check My Bookings shortly." : "The payment wasn't completed or the slot was taken. Please try again."}
          </p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/browse"><Button className="rounded-sm">Back to services</Button></Link>
            <Link to="/my-bookings"><Button variant="outline" className="rounded-sm">My bookings</Button></Link>
          </div>
        </>
      )}
    </div>
  );
}
